import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {map} from "rxjs/operators";

import {CommonTransformationService} from './common-transformation.service';
import {SearchParam} from "@app/shared/enums/search-param.enum";
import {ApprovalStatusType} from "@app/shared/enums/approval-status-type.enum";
import {BillingType, ContractClinInfo, ContractInfo, ProjectInfo, ProjectTask, QueryResult} from "@app/shared";
import {LocationType} from "@app/shared/classes/user";
import {SelectItem} from "primeng/api";

@Injectable()
export class ProjectService {

  constructor(private http: HttpClient, private transform: CommonTransformationService) {
  }

  searchContracts(customerId: number, transaction?: string, currentStatus?: ApprovalStatusType): Observable<QueryResult> {
    let url = `/contract/contracts/search?count=-1&customer.id= ${customerId}`;
    if (transaction) {
      url += `&transaction=${transaction}`;
    }
    if (currentStatus) {
      url += `&currentStatus=${currentStatus}`;
    }
    return this.http.get<QueryResult>(url);
  }

  searchContractInfo(status?: ApprovalStatusType, lineOfBusinessIds?: number[], orgIds?: number[]): Observable<QueryResult> {
    let url = '/contract/contracts/info?count=-1';

    if (status) {
      url += `&currentStatus=${status}`;
    }
    if (lineOfBusinessIds && lineOfBusinessIds.length > 0) {
      url += `&lineOfBusinessId=${lineOfBusinessIds.toString()}`;
    }
    if (orgIds && orgIds.length > 0) {
      url += `&orgId=${orgIds.toString()}`;
    }
    return this.http.get<QueryResult>(url);
  }

  getContractInfoById(contractId: number, lineOfBusinessIds?: number[], orgIds?: number[]): Observable<ContractInfo> {
    let url = `/contract/contracts/info/${contractId}`;
    if (lineOfBusinessIds && lineOfBusinessIds.length > 0) {
      url += `&lineOfBusinessId=${lineOfBusinessIds.toString()}`;
    }
    if (orgIds && orgIds.length > 0) {
      url += `?orgId=${orgIds.toString()}`;
    }
    return this.http.get<ContractInfo>(url);
  }

  getContractInfoAsDropdown(idAsValue?: boolean, status?: ApprovalStatusType, lineOfBusinessIds?: number[], orgIds?: number[]): Observable<SelectItem[]> {
    return this.searchContractInfo(status, lineOfBusinessIds, orgIds).pipe(map(res => {
      if (idAsValue) {
        return this.transform.convertToDropDown("contractTitle", res.list, "id", true);
      } else {
        return this.transform.convertToDropDown("contractTitle", res.list, null, true);
      }
    }));
  }

  getContractSummary(): Observable<QueryResult> {
    const url = '/contract/contracts/summary?count=-1';
    return this.http.get<QueryResult>(url);
  }

  getClinInfoById(clinId: number, orgIds?: number[]): Observable<ContractClinInfo> {
    let url = `/contract/contract-clin-info/${clinId}`;
    if (orgIds && orgIds.length > 0) {
      url += `?orgId=${orgIds.toString()}`;
    }
    return this.http.get<ContractClinInfo>(url);
  }

  searchClinInfo(contractId: number): Observable<QueryResult> {
    const url = `/contract/contract-clin-info/search?count=-1&contract.id=${contractId}`;
    return this.http.get<QueryResult>(url);
  }

  getClinInfoAsDropdown(contractId: number, idAsValue?: boolean): Observable<SelectItem[]> {
    return this.searchClinInfo(contractId).pipe(map(res => {
      if (idAsValue) {
        return this.transform.convertToDropDown("name", res.list, "id", true);
      } else {
        return this.transform.convertToDropDown("name", res.list, null, true);
      }
    }));
  }

  getProjectInfo(name?: string, contractIds?: number[], orgIds?: number[]): Observable<QueryResult> {
    let url = `/projects/info?count=-1`;

    if (name) {
      url += `&name=${name}`;
    }
    if (contractIds && contractIds.length > 0) {
      url += `&contractId=${contractIds.toString()}`;
    } else {
      // FixMe jC: rel-1.3 need to find a way to send null value
      url += `&contractId=-1`;
    }
    if (orgIds && orgIds.length > 0) {
      url += `&orgId=${orgIds.toString()}`;
    }
    return this.http.get<QueryResult>(url);
  }

  getProjectInfoById(projectId: number, orgIds?: number[]): Observable<ProjectInfo> {
    let url = `/projects/info/${projectId}?`;
    if (orgIds && orgIds.length > 0) {
      url += `orgId=${orgIds.toString()}`;
    }
    return this.http.get<ProjectInfo>(url);
  }

  getProjectInfoAsDropdown(name?: string, contractIds?: number[], idAsValue?: boolean, orgIds?: number[]): Observable<SelectItem[]> {
    return this.getProjectInfo(name, contractIds, orgIds).pipe(map(res => {
      if (idAsValue) {
        return this.transform.convertToDropDown("name", res.list, "id", true);
      } else {
        return this.transform.convertToDropDown("name", res.list, null, true);
      }
    }));
  }

  searchProjectSummary(projectId?: number, name?: string, active?: boolean, status?: ApprovalStatusType, contractId?: number, isStandAloneProject?: boolean, excludedBillingType?: BillingType): Observable<QueryResult> {
    let url = `/projects/summary?${SearchParam.MaxReturn}`;
    if (name) {
      url += `&name=${name}`;
    }

    if (active != null) {
      url += `&active=${String(active)}`;
    }

    if (projectId) {
      url += `&project.id=${projectId}`;
    }

    if (status) {
      url += `&currentStatus=${status}`;
    } else {
      url += `&currentStatus=${ApprovalStatusType.Completed}`;
    }

    if (contractId) {
      url += `&contract.id=${contractId}`;
    }

    if (isStandAloneProject) {
      url += `&isStandaloneProject=${isStandAloneProject}`;
    }

    if (excludedBillingType) {
      url += `&excludedBillingType=${excludedBillingType}`;
    }
    return this.http.get<QueryResult>(url);
  }

  getProjectSummaryAsDropdown(projectId?: number, name?: string, active?: boolean, status?: ApprovalStatusType, contractId?: number, isStandAloneProject?: boolean, returnObjValue?: boolean): Observable<any> {
    if (returnObjValue) {
      return this.searchProjectSummary(projectId, name, active, status, contractId, isStandAloneProject).pipe(map(res => {
        return this.transform.convertToDropDown("name", res.list);
      }));
    } else {
      return this.searchProjectSummary(projectId, name, active, status, contractId, isStandAloneProject).pipe(map(res => {
        return this.transform.convertToDropDown("name", res.list, "id");
      }));
    }
  }

  getProjectLaborCategories(projectId: number): Observable<QueryResult> {
    const url = `/projects/labor-categories?count=-1&projectId=${projectId}`;
    return this.http.get<QueryResult>(url);
  }

  getProjectOnsiteWorkLocations(): Observable<QueryResult> {
    let url = `/projects/work-locations/search?${SearchParam.MaxReturn}`;
    url += `&locationType=${LocationType.OnSite}`;
    return this.http.get<QueryResult>(url);
  }

  getProjectTaskById(taskId: number): Observable<ProjectTask> {
    const url = `/projects/tasks/${taskId}`;
    return this.http.get<ProjectTask>(url);
  }
}
